import { createSelector } from 'reselect';
import { getCurrentUserContext, user } from '../../../selectors/ui';
import { getIsPublisherOurStories } from '../../../features/discover/state-management/selectors';
import {
  FEATURE_FLAG_ENTITY_AD_ACCOUNT,
  FEATURE_FLAG_ENTITY_BUSINESS_PROFILE_ID,
  FEATURE_FLAG_ENTITY_ORGANIZATION,
  FEATURE_FLAG_ENTITY_PUBLISHER,
  FEATURE_FLAG_ENTITY_USER,
  GLOBAL_ID,
  IS_SSO,
  OUR_STORIES_PUBLISHER_ID,
  WILDCARD_ID,
} from '../../../constants';
import { groupBy } from '../../../util/array';
import { isNil, isNotNil } from '../../../util/object';
import { hashIsWithinBounds } from '../../../util/numbers';
import { FEATURE_FLAG_NAMES, TRUE } from '../constants';
import { DEFAULT_CHAPTER_LENGTH_TARGET_SECOND } from '../../video-reformatting/constants';

/**
 * @typedef {Object} FeatureFlag
 * @property {String} id
 * @property {String} name
 * @property {String} value
 * One of below:
 * @property {String} [ad_account_id]
 * @property {String} [organization_id]
 * @property {String} [publisher_id]
 * @property {String} [business_profile_id]
 * @property {String} [user_id]
 */

/**
 * Returns all featureFlags
 * @returns {Array.<FeatureFlag>}
 */
export const getAllFeatureFlags = (state) => {
  return state.featureFlags.allFeatureFlags;
};

/**
 * Like {@link getAllFeatureFlags} but filters for currently active org, ad account,
 * and/or publisher account.
 * @returns {Array.<FeatureFlag>}
 */
export const getValidFeatureFlags = createSelector(
  [getAllFeatureFlags, user, getCurrentUserContext],
  (
    allFeatureFlags,
    user,
    { organizationId, adAccountId, publisherId, businessProfileId },
  ) => {
    if (isNil(user)) return [];

    const mapActiveEntities = [];

    if (isNotNil(organizationId)) {
      mapActiveEntities.push([
        FEATURE_FLAG_ENTITY_ORGANIZATION,
        organizationId,
      ]);
    }

    if (isNotNil(adAccountId)) {
      mapActiveEntities.push([FEATURE_FLAG_ENTITY_AD_ACCOUNT, adAccountId]);
    }

    if (isNotNil(publisherId)) {
      mapActiveEntities.push([FEATURE_FLAG_ENTITY_PUBLISHER, publisherId]);
    }

    if (isNotNil(publisherId)) {
      mapActiveEntities.push([
        FEATURE_FLAG_ENTITY_BUSINESS_PROFILE_ID,
        businessProfileId,
      ]);
    }

    const userId = IS_SSO ? user.id : user.email; // https://jira.sc-corp.net/browse/ADS-37488
    if (isNotNil(userId)) {
      mapActiveEntities.push([FEATURE_FLAG_ENTITY_USER, userId]);
    }

    return allFeatureFlags.filter((flag) => {
      return mapActiveEntities.some(([idType, validId]) => {
        // Ensure exists in current scope
        const flagId = flag[idType];
        if (isNil(flagId)) return false;
        return (
          flagId === validId ||
          flagId === GLOBAL_ID ||
          flagId === OUR_STORIES_PUBLISHER_ID ||
          flagId === WILDCARD_ID
        );
      });
    });
  },
);

/**
 * Returns key value pairs of flag names and corresponding values.
 * Filters for flags corresponding to currently active org,
 * ad account, and/or publisher account.
 * Priority = user > ad_account > organization > publisher > snap pro account
 * @returns {Object.<string, string>}
 */
export const getValidFlagValuesByName = createSelector(
  [getValidFeatureFlags, getIsPublisherOurStories],
  (validFeatureFlags, isPublisherOurStories) => {
    const featureFlagsByName = groupBy(validFeatureFlags, 'name');
    const validFeatureFlagValuesByName = {};
    for (const [flagName, featureFlags] of Object.entries(featureFlagsByName)) {
      const findMatchingFlag = (
        flagEntityType,
        useGlobal = false,
        useOurStories = false,
      ) =>
        featureFlags.find((flag) => {
          if (flagEntityType in flag) {
            const entityValue = flag[flagEntityType];
            if (useGlobal) return entityValue === GLOBAL_ID;
            if (useOurStories) {
              return (
                isPublisherOurStories &&
                entityValue === OUR_STORIES_PUBLISHER_ID
              );
            }
            return (
              entityValue !== GLOBAL_ID &&
              entityValue !== OUR_STORIES_PUBLISHER_ID
            );
          }
          return false;
        });
      const matchingFlag =
        findMatchingFlag(FEATURE_FLAG_ENTITY_USER) ||
        findMatchingFlag(FEATURE_FLAG_ENTITY_AD_ACCOUNT) ||
        findMatchingFlag(FEATURE_FLAG_ENTITY_ORGANIZATION) ||
        findMatchingFlag(FEATURE_FLAG_ENTITY_PUBLISHER) ||
        findMatchingFlag(FEATURE_FLAG_ENTITY_PUBLISHER, false, true) ||
        findMatchingFlag(FEATURE_FLAG_ENTITY_AD_ACCOUNT, true) ||
        findMatchingFlag(FEATURE_FLAG_ENTITY_ORGANIZATION, true) ||
        findMatchingFlag(FEATURE_FLAG_ENTITY_PUBLISHER, true) ||
        findMatchingFlag(FEATURE_FLAG_ENTITY_BUSINESS_PROFILE_ID, true);
      if (matchingFlag) {
        validFeatureFlagValuesByName[flagName] = matchingFlag.value;
      }
    }
    return validFeatureFlagValuesByName;
  },
);

/**
 * @param {Object} state
 * @param {String} flagName
 * @returns {String} valid flag value for the  provided flag name
 */
export const getFeatureFlagValueAsString = (state, flagName) => {
  const validFlagValuesByName = getValidFlagValuesByName(state);
  return validFlagValuesByName[flagName];
};

/**
 * @param {Object} state
 * @param {String} flagName
 * @returns {Number} valid flag value as a number or undefined
 */
export const getFeatureFlagValueAsNumber = (state, flagName) => {
  const value = getFeatureFlagValueAsString(state, flagName);
  if (isNotNil(value)) {
    return Number(value);
  }
  return value;
};

const isFeatureEnabled = ({
  fallbackFn = undefined,
  featureConstant = false,
  featureFlagName = undefined,
  rolloutPercentage = 0,
}) => {
  return (state) => {
    // If enabled for all in config, return true
    if (featureConstant) return true;

    if (isNotNil(featureFlagName)) {
      // Assumes "true" and "false" are the values
      const flagValue = getFeatureFlagValueAsString(state, featureFlagName);
      if (flagValue === TRUE) {
        return true;
      }
    }

    const {
      organizationId,
      publisherId,
      businessProfileId,
    } = getCurrentUserContext(state);

    // If there is a desired rollout percentage, evaluate with hash
    if (isNotNil(rolloutPercentage) && rolloutPercentage > 0) {
      if (isNotNil(organizationId)) {
        if (hashIsWithinBounds(organizationId, rolloutPercentage)) {
          return true;
        }
      }

      if (isNotNil(publisherId)) {
        if (hashIsWithinBounds(publisherId, rolloutPercentage)) {
          return true;
        }
      }

      if (isNotNil(businessProfileId)) {
        if (hashIsWithinBounds(businessProfileId, rolloutPercentage)) {
          return true;
        }
      }
    }

    // If there is a fallback function, return its value
    if (isNotNil(fallbackFn)) {
      return fallbackFn(state);
    }

    return false;
  };
};

// Check if Snapcut feature is enabled
export const getIsSnapCutEnabled = isFeatureEnabled({
  featureFlagName: FEATURE_FLAG_NAMES.SNAP_CUT_ENABLED,
});

// Check if "Feature" filter category on Template Picker is allowed
export const getIsTemplateFeatureCategoryEnabled = isFeatureEnabled({
  featureFlagName: FEATURE_FLAG_NAMES.TEMPLATE_FEATURE_CATEGORY,
});

// Is the design switching enabled
export const getIsDesignSwitchingEnabled = isFeatureEnabled({
  featureFlagName: FEATURE_FLAG_NAMES.DESIGN_SWITCHING_ENABLED,
});

// Is the new advanced timeline enabled
export const getIsAdvancedTimelineV2Enabled = isFeatureEnabled({
  featureFlagName: FEATURE_FLAG_NAMES.ADVANCED_TIMELINE_V2_ENABLED,
});

// Is download current frame enabled
export const getIsDownloadCurrentFrameEnabled = isFeatureEnabled({
  featureFlagName: FEATURE_FLAG_NAMES.DOWNLOAD_CURRENT_FRAME,
});

// Is experimental single canvas enabled?
export const getIsSingleCanvasTimelineEnabled = isFeatureEnabled({
  featureFlagName: FEATURE_FLAG_NAMES.SINGLE_CANVAS_TIMELINE,
});

// Allows users to toggle the locked setting in the UI
export const getIsLockedLayersEnabled = isFeatureEnabled({
  featureFlagName: FEATURE_FLAG_NAMES.LOCKED_LAYERS_ENABLED,
});

// Get release notes version
export const getReleaseNotesVersion = (state) => {
  return getFeatureFlagValueAsString(
    state,
    FEATURE_FLAG_NAMES.RELEASE_NOTES_VERSION,
  );
};

// Get target chapter length
export const getTargetChapterLengthFromFeatureFlag = (state) => {
  return (
    getFeatureFlagValueAsNumber(
      state,
      FEATURE_FLAG_NAMES.TARGET_CHAPTER_LENGTH,
    ) || DEFAULT_CHAPTER_LENGTH_TARGET_SECOND
  );
};

export const getEpisodeMinChapterLengthInSecondsFromFeatureFlag = (state) => {
  return getFeatureFlagValueAsNumber(
    state,
    FEATURE_FLAG_NAMES.EPISODE_MIN_SNAP_LENGTH_IN_SECONDS,
  );
};

// Is chapter breaks in Video Conversion enabled
export const getIsChapterBreaksEnabled = isFeatureEnabled({
  featureFlagName: FEATURE_FLAG_NAMES.CHAPTER_BREAKS_ENABLED,
});

// Is chapter breaks's visibility defaulted to visible in Video Conversion enabled
export const getIsChapterBreaksShownByDefaultEnabled = isFeatureEnabled({
  featureFlagName: FEATURE_FLAG_NAMES.CHAPTER_BREAKS_SHOWN_BY_DEFAULT_ENABLED,
});

// Check if Snap Preview V2 is enabled
export const getIsSendToStoryStudioEnabled = isFeatureEnabled({
  featureFlagName: FEATURE_FLAG_NAMES.SEND_TO_STORY_STUDIO_ENABLED,
});

// Check if Creative Dimensions Control is enabled
export const getIsCreativeDimensionsControlEnabled = isFeatureEnabled({
  featureFlagName: FEATURE_FLAG_NAMES.CREATIVE_DIMENSIONS_CONTROL_ENABLED,
});

// Check if Multiple Video Clips in Video Conversion is enabled
export const getIsMultipleVideoClipsEnabled = isFeatureEnabled({
  featureFlagName: FEATURE_FLAG_NAMES.MULTI_VIDEO_CLIPS_ENABLED,
});

export const getIsAudioTrimmingEnabled = isFeatureEnabled({
  featureFlagName: FEATURE_FLAG_NAMES.AUDIO_TRIMMING_ENABLED,
});

export const getIsVISMediaUploadForAllEditorsEnabled = isFeatureEnabled({
  featureFlagName: FEATURE_FLAG_NAMES.VIS_MEDIA_UPLOAD_FOR_ALL_EDITORS_ENABLED,
});

export const getIsAutoLayoutEnabled = isFeatureEnabled({
  featureFlagName: FEATURE_FLAG_NAMES.AUTO_LAYOUT_ENABLED,
});

export const getIsClipFromShotsEnabled = isFeatureEnabled({
  featureFlagName: FEATURE_FLAG_NAMES.CLIP_FROM_SHOTS_ENABLED,
});
